import { Injectable } from '@angular/core';
import {
  VOSK_DICTATION_MODELS,
  VOSK_RECOGNIZER_PROCESSOR_URL,
  VoskDictationModelId,
  VoskDictationModelOption
} from '../config/vosk-dictation.config';

type DictationCallbacks = {
  onPartial?: (text: string) => void;
  onResult?: (text: string) => void;
};

@Injectable({
  providedIn: 'root'
})
export class ClientDictationService {
  private modelInstance: any | null = null;
  private recognizer: any | null = null;
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private messageChannel: MessageChannel | null = null;
  private sessionActive = false;
  private startPromise: Promise<void> | null = null;

  readonly modelOptions = VOSK_DICTATION_MODELS.models;

  get selectedModel(): VoskDictationModelOption {
    return this.modelOptions[0];
  }

  async start(callbacks: DictationCallbacks = {}): Promise<void> {
    if (this.startPromise) {
      return this.startPromise;
    }

    this.startPromise = this.startInternal(callbacks).finally(() => {
      this.startPromise = null;
    });

    return this.startPromise;
  }

  async stop(): Promise<string> {
    this.sessionActive = false;

    const recognizer = this.recognizer;

    const finalTextPromise = new Promise<string>(resolve => {
      if (!recognizer?.retrieveFinalResult) {
        resolve('');
        return;
      }

      let settled = false;
      const finish = (text: string) => {
        if (settled) return;
        settled = true;
        resolve(text);
      };

      recognizer.on('result', (message: any) => {
        finish(message?.result?.text ?? '');
      });

      try {
        recognizer.retrieveFinalResult();
      } catch {
        finish('');
        return;
      }

      setTimeout(() => finish(''), 900);
    });

    this.workletNode?.disconnect();
    this.sourceNode?.disconnect();

    this.stream?.getTracks().forEach(track => track.stop());
    this.stream = null;

    const finalText = await finalTextPromise;

    if (this.audioContext && this.audioContext.state !== 'closed') {
      try {
        await this.audioContext.close();
      } catch {
        // ignore
      }
    }
    this.audioContext = null;

    if (recognizer?.remove) {
      try {
        recognizer.remove();
      } catch {
        // ignore
      }
    }
    this.recognizer = null;
    this.workletNode = null;
    this.sourceNode = null;
    this.messageChannel = null;

    if (this.modelInstance?.terminate) {
      try {
        this.modelInstance.terminate();
      } catch {
        // ignore
      }
    }
    this.modelInstance = null;

    return finalText.trim();
  }

  private async startInternal(callbacks: DictationCallbacks): Promise<void> {
    await this.stop();

    const Vosk = await import('vosk-browser');
    this.modelInstance = await this.loadModel(Vosk);

    this.messageChannel = new MessageChannel();
    this.modelInstance.registerPort(this.messageChannel.port1);

    const sampleRate = 48000;

    const stream = await navigator.mediaDevices.getUserMedia({
      video: false,
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        channelCount: 1,
        sampleRate
      }
    });

    const recognizer = new this.modelInstance.KaldiRecognizer(sampleRate);

    recognizer.on('result', (message: any) => {
      if (!this.sessionActive) return;
      callbacks.onResult?.(message?.result?.text ?? '');
    });

    recognizer.on('partialresult', (message: any) => {
      if (!this.sessionActive) return;
      callbacks.onPartial?.(message?.result?.partial ?? '');
    });

    const audioContext = new AudioContext({ sampleRate });
    await audioContext.audioWorklet.addModule(VOSK_RECOGNIZER_PROCESSOR_URL);

    const workletNode = new AudioWorkletNode(audioContext, 'recognizer-processor', {
      channelCount: 1,
      numberOfInputs: 1,
      numberOfOutputs: 1
    });

    workletNode.port.postMessage(
      { action: 'init', recognizerId: recognizer.id },
      [this.messageChannel.port2]
    );

    const sourceNode = audioContext.createMediaStreamSource(stream);
    sourceNode.connect(workletNode);
    workletNode.connect(audioContext.destination);

    // Worker-side recognizer init is async; brief pause avoids early drops.
    await this.delay(800);

    this.sessionActive = true;
    this.stream = stream;
    this.audioContext = audioContext;
    this.sourceNode = sourceNode;
    this.workletNode = workletNode;
    this.recognizer = recognizer;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async loadModel(voskModule: any): Promise<any> {
    const urls = [
      this.selectedModel.url,
      '/assets/vosk-models/vosk-model-small-en-us-0.15.tar.gz'
    ];

    let lastError: unknown;
    for (const url of urls) {
      try {
        const model = await voskModule.createModel(url);
        if (model?.ready) {
          return model;
        }
      } catch (error) {
        lastError = error;
        console.warn('Vosk model load failed for', url, error);
      }
    }

    throw lastError ?? new Error('Speech model failed to load.');
  }
}
